import { normalizeErrorString } from '@medplum/core'
import type {
  ExtractResource,
  Resource,
  ResourceType,
} from '@medplum/fhirtypes'
import { invariant } from '@seen/core/invariant'
import {
  type UseMutationOptions,
  type UseMutationResult,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { toast } from 'sonner'
import { cacheKeys } from '#src/hooks/cacheKeys.ts'
import {
  ifMatchHeader,
  isVersionConflictError,
  ResourceVersionConflictError,
} from '#src/hooks/mutationConflicts.ts'
import { reportPutConflict } from '#src/hooks/mutationObservability.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'

export type UseFhirSaveMutationArgs<T extends ResourceType> = ExtractResource<T>

export interface UseFhirSaveMutationOptions<T extends ResourceType> {
  mutation?: Omit<
    UseMutationOptions<ExtractResource<T>, unknown, UseFhirSaveMutationArgs<T>>,
    'mutationFn'
  >
  /**
   * Invoked when a PUT fails because the resource was modified between our
   * read and write (HTTP 412). Receives the fresh server version of the
   * resource so the caller can rehydrate form state — typically via
   * `form.reset(latest, { keepDirtyValues: true })` — without losing the
   * user's in-progress edits.
   *
   * If omitted, the conflict is surfaced as a toast and the caller must
   * refresh manually.
   */
  onVersionConflict?: (latest: Resource) => void
}

export function useFhirSaveMutation<T extends ResourceType>(
  type: T,
  options?: UseFhirSaveMutationOptions<T>
): UseMutationResult<ExtractResource<T>, unknown, UseFhirSaveMutationArgs<T>> {
  const medplum = useMedplum()
  const queryClient = useQueryClient()
  const isReadOnly = useIsReadOnly()

  return useMutation({
    ...options?.mutation,
    onSuccess: (data, variables, context) => {
      invariant(data.id, 'ID is required')
      cacheKeys.invalidateMutation(queryClient, type, data.id)
      queryClient.setQueryData(cacheKeys.read(type, data.id), data)
      options?.mutation?.onSuccess?.(data, variables, context)
    },
    onError: (error, variables, context) => {
      if (error instanceof ResourceVersionConflictError) {
        toast.error(
          'This record was updated by someone else. Your changes are preserved — please review and save again.'
        )
      } else {
        toast.error(normalizeErrorString(error))
      }
      options?.mutation?.onError?.(error, variables, context)
    },
    mutationFn: async (args) => {
      if (isReadOnly) {
        console.warn(
          '[read-only] mutation attempted: useFhirSaveMutation',
          type
        )
      }

      if (!args.id) {
        return medplum.createResource(args)
      }

      const fromVersionId = args.meta?.versionId
      const headers = ifMatchHeader(fromVersionId)

      try {
        return await medplum.updateResource(args, headers ? { headers } : {})
      } catch (error) {
        if (!isVersionConflictError(error)) {
          throw error
        }

        const latest = (await medplum.readResource(
          type,
          args.id
        )) as ExtractResource<T>

        // Heal the stale cache so any other component reading this
        // resource sees the fresh version.
        queryClient.setQueryData(cacheKeys.read(type, args.id), latest)

        reportPutConflict({
          resourceType: type,
          id: args.id,
          fromVersionId,
          toVersionId: latest.meta?.versionId,
        })

        options?.onVersionConflict?.(latest)

        throw new ResourceVersionConflictError({
          fhirResourceType: type,
          resourceId: args.id,
          fromVersionId,
          latest,
          cause: error,
        })
      }
    },
  })
}

export { ResourceVersionConflictError }
